/**
 * SliderControl — Generic discrete-step range slider.
 *
 * Maps slider positions to native filter option values.
 * Supports any number of discrete steps with display names.
 */
export default class SliderControl {
  /**
   * @param {UIBridge} bridge
   * @param {string} filterName
   * @param {string} containerSelector - CSS selector for the .property-card
   * @param {Object} config
   * @param {string[]} config.values - Native option values (sent to hidden select)
   * @param {string[]} config.labels - Display names shown in the property-value pill
   */
  constructor(bridge, filterName, containerSelector, config) {
    this.bridge = bridge;
    this.filterName = filterName;
    this.container = document.querySelector(containerSelector);
    this.values = config.values;
    this.labels = config.labels;

    if (!this.container) {
      console.warn(`SliderControl: container not found for ${filterName}`);
      return;
    }

    this.slider = this.container.querySelector('.custom-slider');
    this.valueDisplay = this.container.querySelector('.property-value');

    this._init();

    bridge.register(filterName, {
      nativeSelector: this._getNativeSelector(),
      type: 'select',
      onNativeChange: (value) => this._onCascadeUpdate(value)
    });
  }

  _init() {
    if (!this.slider) return;

    this.slider.addEventListener('input', () => {
      const idx = parseInt(this.slider.value);
      this._updateDisplay(idx);
      this._highlightLabel(idx);
      this.bridge.setFilterValue(this.filterName, this.values[idx]);
    });

    this.slider.addEventListener('change', () => {
      const idx = parseInt(this.slider.value);
      this.bridge.setFilterValue(this.filterName, this.values[idx]);
    });

    // Make step labels clickable
    this._initClickableLabels();
  }

  _initClickableLabels() {
    // Discrete step labels (Structure, Class, Application, Resistance)
    const stepLabels = this.container.querySelectorAll('.slider-step-labels span');
    stepLabels.forEach((span, idx) => {
      span.addEventListener('click', () => {
        if (idx < this.values.length) {
          this.slider.value = idx;
          this._updateDisplay(idx);
          this._highlightLabel(idx);
          this.bridge.setFilterValue(this.filterName, this.values[idx]);
        }
      });
    });
    this.stepLabels = stepLabels;

    // Min/max labels (Insulation)
    const minMaxLabels = this.container.querySelectorAll('.slider-labels span');
    if (minMaxLabels.length === 2) {
      minMaxLabels[0].addEventListener('click', () => {
        this.slider.value = 0;
        this._updateDisplay(0);
        this.bridge.setFilterValue(this.filterName, this.values[0]);
      });
      minMaxLabels[1].addEventListener('click', () => {
        const lastIdx = this.values.length - 1;
        this.slider.value = lastIdx;
        this._updateDisplay(lastIdx);
        this.bridge.setFilterValue(this.filterName, this.values[lastIdx]);
      });
    }

    // Highlight initial label
    if (stepLabels.length > 0) {
      this._highlightLabel(parseInt(this.slider.value));
    }
  }

  _highlightLabel(activeIdx) {
    if (!this.stepLabels) return;
    this.stepLabels.forEach((span, idx) => {
      span.classList.toggle('active', idx === activeIdx);
    });
  }

  _updateDisplay(idx) {
    if (this.valueDisplay && idx >= 0 && idx < this.labels.length) {
      this.valueDisplay.textContent = this.labels[idx];
    }
    // Also update thumb-value overlay if present
    const thumbVal = document.getElementById('slider-thumb-value');
    if (thumbVal && this.filterName === 'insulation' && idx >= 0 && idx < this.labels.length) {
      thumbVal.textContent = this.labels[idx];
    }
  }

  _onCascadeUpdate(value) {
    const idx = this.values.indexOf(value);
    if (idx !== -1 && this.slider) {
      this.slider.value = idx;
      this._updateDisplay(idx);
      this._highlightLabel(idx);
    } else {
      // Partial match fallback
      for (let i = 0; i < this.values.length; i++) {
        if (value.includes(this.values[i]) || this.values[i].includes(value)) {
          this.slider.value = i;
          this._updateDisplay(i);
          this._highlightLabel(i);
          break;
        }
      }
    }
  }

  _getNativeSelector() {
    const map = {
      'structure': '.structure',
      'manufacturer': '.manufacturer',
      'class': '.class',
      'insulation': '.insulation',
      'application': '.application',
      'resistance': '.resistance'
    };
    return map[this.filterName] || `.${this.filterName}`;
  }
}
